import { UploadFile } from 'antd';
import { Moment } from 'moment';

export enum PaymentDateType {
  creditedToBankAccount = 'CREDITED_TO_BANK_ACCOUNT',
  draweePaysTheBill = 'DRAWEE_PAYS_THE_BILL',
}

export const PaymentDateTypeLabel = {
  [PaymentDateType.creditedToBankAccount]: 'Créditado na conta bancária',
  [PaymentDateType.draweePaysTheBill]: 'Pagador pagar o boleto',
};

export interface BankAccountSettingsFormValues {
  general: {
    agreementNumber: string;
    agreementDescription: string;
    agreementWallet: string;
    agreementWalletCode: string;
    agreementSpecies: string;
    agreementCnabPattern: string;
    agreementLayoutFileVersion: string;
    agreementContractNumber: string;
    agreementRemittanceDensity: string;
    agreementInstantRegister: number;
    agreementRestartDaily: number;
    agreementStation: string;
    agreementBankOurNumber: number;
    agreementRemittanceNumber: string;
    agreementWsConsult: number;
    agreementHybridEmission: number;
    agreementCurrentOurNumber: number;

    beneficiaryCode: string;
    codeCompanyBank: string;

    //BankFee
    bankFee?: number;
    hasBankFee?: boolean;
    planAccountId?: number;

    //Basic
    titleAcceptance?: TitleAcceptance;
    titleDocumentSpecies?: string;
    titleVariationWallet?: string;

    //Certificate
    agreementApiId: string;
    agreementApiKey: string;
    agreementApiSecret: string;
    agreementWebServiceType: string;

    paymentDateType: PaymentDateType;

    //Ailos
    agreementAilosPassword?: string;
    agreementSandboxEnvironment?: number;

    //BB
    titleCodeReference?: string;

    //Caixa
    titleIdentificationTypePayment?: string;
  };

  certificate: {
    certificateFile: UploadFile[];
    certificatePassword: string;
    certificateAlias: string;
    certificateEmail: string;
    certificateExpirationDate: Moment;
  };

  //Discount
  discount?: BankAccountSettingsDiscount;

  //Interest
  interest?: {
    titleInterestValue?: number;
    titleInterestDays?: number;
    titleInterestCode?: TitleInterestCode;
  };

  //Fine
  fine?: {
    titleFineCode?: string;
    titleFineDays?: number;
    titleFineRateValue?: number;
  };

  //Protest
  protest?: {
    titleProtestCode?: string;
    titleProtestTerm?: string;
  };

  //Messages
  messages?: BankAccountSettingsMessages[];
}

export interface BankAccountSettingsDiscount {
  titleDiscountCode?: string;
  titleDiscountDays?: number;
  titleDiscountRateValue?: string;
  titleDiscountValue?: string;
  titleDiscountCode2?: string;
  titleDiscountDays2?: number;
  titleDiscountRateValue2?: string;
  titleDiscountCode3?: string;
  titleDiscountDays3?: number;
  titleDiscountRateValue3?: string;
}

export interface BankAccountSettingsMessages {
  titleMessage?: string;
}

export type BankConfigFields = Partial<
  Record<
    string,
    {
      required?: boolean;
      initialValue?: unknown;
      options?: string[];
      maxLength?: number;
      visible?: boolean;
      disabled?: boolean;
      helpMessage?: string;
      fractions?: number;
    }
  >
>;

export interface BankConfig {
  [key: string]: {
    name: string;
    fields: BankConfigFields;
  };
}

export interface BankAccountConfigTypeDocEspecie {
  code: string;
  description: string;
}

export enum TitleInterestCode {
  valuePerDay = '1',
  monthlyRate = '2',
  exempt = '3',
}

export enum TitleAcceptance {
  notAccepted = 'N',
  accepted = 'S',
}

export interface BankAccountSettings {
  bankAccountAgreement: BankAccountSettingsAgreement;
  id: number;

  //Basic
  titleAcceptance: TitleAcceptance;
  titleDocumentSpecies: string;
  titleVariationWallet: string;

  //Interest
  titleInterestCode: TitleInterestCode;
  titleInterestDate: string;
  titleInterestDays: number;
  titleInterestValue: number;

  //Discount
  titleDiscountCode?: string;
  titleDiscountCode2?: string;
  titleDiscountCode3?: string;
  titleDiscountDate?: string;
  titleDiscountDate2?: string;
  titleDiscountDate3?: string;
  titleDiscountDays?: number;
  titleDiscountDays2?: number;
  titleDiscountDays3?: number;
  titleDiscountRateValue?: string;
  titleDiscountRateValue2?: string;
  titleDiscountRateValue3?: string;
  titleDiscountValue?: string;

  //Fine
  titleFineCode?: string;
  titleFineDate?: string;
  titleFineDays?: number;
  titleFineRateValue?: number;

  //Messages
  titleMessage01?: string;
  titleMessage02?: string;
  titleMessage03?: string;
  titleMessage04?: string;
  titleMessage05?: string;
  titleMessage06?: string;
  titleMessage07?: string;
  titleMessage08?: string;
  titleMessage09?: string;

  //Protest
  titleProtestCode?: string;
  titleProtestTerm?: string;

  //Certificate
  certificate?: {
    id: number;
    certificateFile: string;
    certificatePassword: string;
    certificateAlias: string;
    certificateEmail: string;
    expirationDate: string;
  };

  paymentDateType: PaymentDateType;

  hasBankFee?: boolean;
  bankFee?: number;
  planAccount?: {
    id: number;
  };
  titleCodeReference?: string;

  titleIdentificationTypePayment?: string;
}

export interface BankAccountSettingsAgreement {
  agreementApiId: string;
  agreementApiKey: string;
  agreementApiSecret: string;
  agreementWebServiceType: string;
  agreementBankOurNumber?: boolean;
  agreementCnabPattern: string;
  agreementContractNumber: string;
  agreementDescription: string;
  agreementInstantRegister?: boolean;
  agreementLayoutFileVersion: string;
  agreementNumber: string;
  agreementRemittanceDensity: string;
  agreementRemittanceNumber: string;
  agreementRestartDaily?: boolean;
  agreementSpecies: string;
  agreementStation: string;
  agreementWallet: string;
  agreementWalletCode: string;
  agreementHybridEmission?: boolean;
  agreementWsConsult?: boolean;
  agreementCurrentOurNumber?: number;
  agreementAilosPassword?: string;
  agreementSandboxEnvironment?: boolean;
}

export interface BankAccountSettingsCreateRequest {
  bankAccountAgreement: BankAccountSettingsAgreement;
  bankAccountId: number;

  //Basic
  titleAcceptance?: TitleAcceptance;
  titleDocumentSpecies?: string;

  //BankFee
  bankFee?: number;
  hasBankFee?: boolean;
  planAccountId?: number;

  //Interest
  titleInterestCode?: TitleInterestCode;
  titleInterestDate?: string;
  titleInterestValue?: string;

  //Discount
  titleDiscountCode?: string;
  titleDiscountCode2?: string;
  titleDiscountCode3?: string;
  titleDiscountDate?: string;
  titleDiscountDate2?: string;
  titleDiscountDate3?: string;
  titleDiscountRateValue?: string;
  titleDiscountRateValue2?: string;
  titleDiscountRateValue3?: string;
  titleDiscountValue?: string;

  //Fine
  titleFineCode?: string;
  titleFineDate?: string;
  titleFineRateValue?: string;

  //Messages
  titleMessage01?: string;
  titleMessage02?: string;
  titleMessage03?: string;
  titleMessage04?: string;
  titleMessage05?: string;
  titleMessage06?: string;
  titleMessage07?: string;
  titleMessage08?: string;
  titleMessage09?: string;

  //Protest
  titleProtestCode?: string;
  titleProtestTerm?: string;

  paymentDateType: PaymentDateType;
}

export interface BankAccountSettingsUpdateRequest {
  bankAccountAgreement: BankAccountSettingsAgreement;
  bankAccountId: number;
  //Basic
  titleAcceptance?: TitleAcceptance;
  titleDocumentSpecies?: string;
  titleVariationWallet?: string;

  titleCodeReference?: string;

  //BankFee
  bankFee?: number;
  hasBankFee?: boolean;
  planAccountId?: number;

  //Interest
  titleInterestCode?: TitleInterestCode;
  titleInterestDays?: number;
  titleInterestValue?: string;

  //Discount
  titleDiscountCode?: string;
  titleDiscountCode2?: string;
  titleDiscountCode3?: string;
  titleDiscountDays?: number;
  titleDiscountDays2?: number;
  titleDiscountDays3?: number;
  titleDiscountRateValue?: string;
  titleDiscountRateValue2?: string;
  titleDiscountRateValue3?: string;
  titleDiscountValue?: string;

  //Fine
  titleFineCode?: string;
  titleFineDays?: number;
  titleFineRateValue?: string;

  //Messages
  titleMessage01?: string;
  titleMessage02?: string;
  titleMessage03?: string;
  titleMessage04?: string;
  titleMessage05?: string;
  titleMessage06?: string;
  titleMessage07?: string;
  titleMessage08?: string;
  titleMessage09?: string;

  //Protest
  titleProtestCode?: string;
  titleProtestTerm?: string;

  titleIdentificationTypePayment?: string;

  certificate?: {
    certificatePassword: string;
    certificateAlias: string;
    certificateEmail: string;
  };

  paymentDateType: PaymentDateType;
}
